import React from 'react';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Chip,
  useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  iconColor?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'error'
    | 'warning'
    | 'info';
  breadcrumbs?: BreadcrumbItem[];
  action?: React.ReactNode;
  badge?: {
    label: string;
    color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
  };
}

const gradientMap: Record<string, string> = {
  primary: 'linear-gradient(135deg, #1E23FE 0%, #0D9E6F 100%)',
  secondary: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
  success: 'linear-gradient(135deg, #0CA963 0%, #087A54 100%)',
  error: 'linear-gradient(135deg, #F44336 0%, #C62828 100%)',
  warning: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
  info: 'linear-gradient(135deg, #1374A1 0%, #0E5A7D 100%)',
};

export default function PageHeader({
  title,
  subtitle,
  icon,
  iconColor = 'primary',
  breadcrumbs,
  action,
  badge,
}: PageHeaderProps) {
  const theme = useTheme();

  return (
    <Box sx={{ mb: 4 }}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            return crumb.path && !isLast ? (
              <Link
                key={index}
                component={RouterLink}
                to={crumb.path}
                underline="hover"
                color="text.secondary"
                sx={{
                  fontSize: '0.875rem',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                {crumb.label}
              </Link>
            ) : (
              <Typography
                key={index}
                color="text.primary"
                sx={{ fontSize: '0.875rem', fontWeight: 500 }}
              >
                {crumb.label}
              </Typography>
            );
          })}
        </Breadcrumbs>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {icon && (
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                background: gradientMap[iconColor] || gradientMap.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 4px 14px ${
                  theme.palette[iconColor]?.main || theme.palette.primary.main
                }40`,
              }}
            >
              {React.cloneElement(icon as React.ReactElement, {
                sx: { color: 'white', fontSize: 28 },
              })}
            </Box>
          )}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                }}
              >
                {title}
              </Typography>
              {badge && (
                <Chip
                  label={badge.label}
                  size="small"
                  color={badge.color || 'primary'}
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Box>
            {subtitle && (
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        {action && <Box>{action}</Box>}
      </Box>
    </Box>
  );
}
